import { useState } from 'react';

const UTILITIES = ['Gas', 'Water', 'Storm', 'Sanitary', 'Electric', 'Pipeline', 'Communication', 'Fiber'];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Build the working rows: always show the 8 standard utilities (merging any saved
// values) plus any extra rows the user added for multiple companies.
function buildRows(saved) {
  const arr = Array.isArray(saved) ? saved : [];
  const fixedByType = {};
  const extras = [];
  for (const r of arr) {
    if (r.fixed && fixedByType[r.type] === undefined) fixedByType[r.type] = r;
    else if (!r.fixed) extras.push(r);
  }
  const standard = UTILITIES.map((type) => ({
    type,
    collected: !!(fixedByType[type] && fixedByType[type].collected),
    owner: (fixedByType[type] && fixedByType[type].owner) || '',
    state: (fixedByType[type] && fixedByType[type].state) || '',
    city: (fixedByType[type] && fixedByType[type].city) || '',
    contact_name: (fixedByType[type] && fixedByType[type].contact_name) || '',
    contact_info: (fixedByType[type] && fixedByType[type].contact_info) || '',
    fixed: true,
  }));
  const extraRows = extras.map((e) => ({
    type: e.type || UTILITIES[0],
    collected: !!e.collected,
    owner: e.owner || '',
    state: e.state || '',
    city: e.city || '',
    contact_name: e.contact_name || '',
    contact_info: e.contact_info || '',
    fixed: false,
  }));
  return [...standard, ...extraRows];
}

export default function RecordsNotesModal({ job, readOnly, onClose, onSave }) {
  const [text, setText] = useState(job.notes || '');
  const [rows, setRows] = useState(() => buildRows(job.utilities));
  const [saving, setSaving] = useState(false);

  const update = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { type: UTILITIES[0], collected: false, owner: '', state: '', city: '', contact_name: '', contact_info: '', fixed: false }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  async function save() {
    setSaving(true);
    await onSave({ notes: text, utilities: rows });
    onClose();
  }

  const inputStyle = { backgroundColor: '#0f1a29', borderColor: '#1e3a5f' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-4xl rounded-xl border border-navy-500 p-6 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#142236' }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-white font-bold text-lg">📝 Records Note</h2>
            <p className="text-gray-400 text-xs mt-0.5 font-mono">{job.project_number}{job.site_name ? ` — ${job.site_name}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
        </div>

        {/* Note */}
        {readOnly ? (
          <div className="rounded px-3 py-2 text-sm text-gray-200 whitespace-pre-wrap min-h-[60px] border mb-4" style={inputStyle}>
            {text || <span className="text-gray-600 italic">No note.</span>}
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Add a note…"
            className="w-full rounded px-3 py-2 text-sm text-white placeholder-gray-500 border focus:outline-none resize-y mb-4"
            style={inputStyle}
          />
        )}

        {/* Utility records */}
        <h3 className="text-xs text-gray-400 uppercase tracking-widest mb-2">Utility Records — collected?</h3>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!r.collected}
                disabled={readOnly}
                onChange={(e) => update(i, { collected: e.target.checked })}
                className="w-4 h-4 shrink-0 accent-green-500"
                title="Collected?"
              />
              {r.fixed ? (
                <span className="text-sm w-28 shrink-0 font-semibold" style={{ color: r.collected ? '#4ade80' : '#d1d5db' }}>{r.type}</span>
              ) : (
                <select
                  value={r.type}
                  disabled={readOnly}
                  onChange={(e) => update(i, { type: e.target.value })}
                  className="text-xs rounded px-1.5 py-1 border w-28 shrink-0 text-white focus:outline-none"
                  style={inputStyle}
                >
                  {UTILITIES.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
              <input
                value={r.owner}
                disabled={readOnly}
                onChange={(e) => update(i, { owner: e.target.value })}
                placeholder="Utility owner"
                className="flex-1 min-w-0 text-xs rounded px-2 py-1 border text-white placeholder-gray-600 focus:outline-none"
                style={inputStyle}
              />
              <select
                value={r.state}
                disabled={readOnly}
                onChange={(e) => update(i, { state: e.target.value })}
                className={`text-xs rounded px-1 py-1 border w-16 shrink-0 focus:outline-none ${r.state ? 'text-white' : 'text-gray-600'}`}
                style={inputStyle}
                title="State"
              >
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                value={r.city}
                disabled={readOnly}
                onChange={(e) => update(i, { city: e.target.value })}
                placeholder="City"
                className="w-24 shrink-0 text-xs rounded px-2 py-1 border text-white placeholder-gray-600 focus:outline-none"
                style={inputStyle}
              />
              <input
                value={r.contact_name}
                disabled={readOnly}
                onChange={(e) => update(i, { contact_name: e.target.value })}
                placeholder="Contact name"
                className="flex-1 min-w-0 text-xs rounded px-2 py-1 border text-white placeholder-gray-600 focus:outline-none"
                style={inputStyle}
              />
              <input
                value={r.contact_info}
                disabled={readOnly}
                onChange={(e) => update(i, { contact_info: e.target.value })}
                placeholder="Phone / email"
                className="flex-1 min-w-0 text-xs rounded px-2 py-1 border text-white placeholder-gray-600 focus:outline-none"
                style={inputStyle}
              />
              {!r.fixed && !readOnly && (
                <button onClick={() => removeRow(i)} className="text-gray-500 hover:text-red-400 text-sm shrink-0" title="Remove">✕</button>
              )}
            </div>
          ))}
        </div>
        {!readOnly && (
          <button onClick={addRow} className="mt-2 text-xs font-bold" style={{ color: '#c9a84c' }}>+ Add another company / utility</button>
        )}

        {/* Footer */}
        <div className="flex gap-3 mt-6">
          {readOnly ? (
            <button onClick={onClose} className="flex-1 py-2 rounded border border-navy-500 text-gray-300 text-sm hover:text-white hover:border-gray-400 transition">Close</button>
          ) : (
            <>
              <button onClick={save} disabled={saving} className="flex-1 bg-gold-500 text-navy-900 py-2 rounded font-bold uppercase tracking-wide text-sm hover:bg-gold-400 disabled:opacity-50 transition">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded border border-navy-500 text-gray-400 text-sm hover:text-white hover:border-gray-400 transition">Cancel</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
